<script setup lang="ts">
/**
 * A labelled field. The control goes in the default slot and receives `id`
 * for the label and `describedby` for its `aria-describedby`, which links it
 * to the error or the hint. The error is a polite live region, rendered even
 * when empty: a region inserted with its text is often not announced, and an
 * assertive one would interrupt the user at every keystroke.
 */
import { computed, useId } from 'vue'

const p = defineProps<{ label: string; hint?: string; error?: string; required?: boolean }>()
const id = useId()
const errorId = `${id}-error`
const hintId = `${id}-hint`
const describedby = computed(() => (p.error ? errorId : p.hint ? hintId : undefined))
</script>

<template>
  <!-- content-start: in a row of fields, the shorter ones do not stretch their label away from their control. -->
  <div class="grid content-start">
    <label :for="id" class="mb-[5px] text-[12px] font-medium text-muted-foreground">{{ label }}<span v-if="required" aria-hidden="true" class="text-danger">{{ ' *' }}</span></label>
    <slot :id="id" :describedby="describedby" />
    <!-- The message comes in softly (each new one again); the region stays in place, so it is still announced. -->
    <span :id="errorId" aria-live="polite" :class="['text-[12px] text-danger', error && 'mt-[5px]']"><span v-if="error" :key="error" class="block animate-rise-in">{{ error }}</span></span>
    <span v-if="hint && !error" :id="hintId" class="mt-[5px] text-[12px] text-muted-foreground">{{ hint }}</span>
  </div>
</template>
